import { useCallback, useContext, useMemo } from "react";
import { Alert } from "../components/Alert";
import { useI18n } from "./i18n-hooks";
import { useMutation } from "@apollo/client";
import { DELETE_CURRENT_PROFILE } from "../graphql/identity";
import { useSnackbar } from "./snackbar";
import { ProfileType } from "../__generated__/graphql";
import { AuthManagerContext } from "../contexts/AuthManager";
import { KEYSTORE_DEFAULT_PROFILE_ID } from "../contexts/ProfileSwitcherManager";
import AsyncStorage from "@react-native-async-storage/async-storage";

type UseDeleteCurrentProfileProps = {
    profileType: ProfileType;
    isPrivateClient?: boolean;
    profileId?: string;
};

export const useDeleteCurrentProfile = ({
    profileType,
    isPrivateClient = false,
    profileId,
}: UseDeleteCurrentProfileProps) => {
    const i18n = useI18n();
    const [deleteCurrentProfile] = useMutation(
        DELETE_CURRENT_PROFILE,
        profileId ? { context: { liftProfile: profileId } } : undefined,
    );
    const { showSnackbar } = useSnackbar();
    const { refreshUser } = useContext(AuthManagerContext);
    const fullProfileType = useMemo(
        () => `${isPrivateClient ? "private_" : ""}${profileType}`,
        [isPrivateClient, profileType],
    );

    const onDeleteCurrentProfile = useCallback(() => {
        return new Promise((resolve) => {
            Alert.alert(
                i18n.t(`profile.delete.${fullProfileType}.title`),
                i18n.t(`profile.delete.${fullProfileType}.description`),
                [
                    {
                        text: i18n.t(`profile.delete.${fullProfileType}.confirm`),
                        onPress: () => {
                            const asyncFunc = async () => {
                                const deleteResult = await deleteCurrentProfile();

                                if (deleteResult.data?.deleteCurrentProfile) {
                                    void AsyncStorage.removeItem(KEYSTORE_DEFAULT_PROFILE_ID);
                                    const newUser = await refreshUser?.();

                                    if (!newUser) {
                                        return;
                                    }

                                    showSnackbar({
                                        text: i18n.t(`profile.delete.${fullProfileType}.success`),
                                        mode: "success",
                                    });
                                    resolve(null);
                                } else {
                                    Alert.alert(
                                        i18n.t("profile.delete_account_error"),
                                        i18n.t(
                                            `profile.delete.${fullProfileType}.error_description`,
                                        ),
                                    );
                                    resolve(null);
                                }
                            };
                            void asyncFunc();
                        },
                        style: "destructive",
                    },
                    {
                        text: i18n.t("cancel"),
                        style: "cancel",
                    },
                ],
                { cancelable: true },
            );
        });
    }, [i18n, fullProfileType, deleteCurrentProfile, refreshUser, showSnackbar]);

    return onDeleteCurrentProfile;
};
